import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc } from '@kanalytics';
import { ContentEventBloc } from '@kbloc';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { DialogueBlockEventType } from '@kp/dialogue/models/dialogue-block-event.model';
import { DialogueBlockType } from '@kp/dialogue/models/dialogue-block.model';
import {
  CLOSEABLE_DIALOGUES_KEYS,
  DAILY_REFLECTION_KEYS,
  Dialogue,
  DialogueLogicKey,
  DIALOGUES_USING_REF_KEYS,
  KumanuKeyType,
} from '@kp/dialogue/models/dialogue.model';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { PulseSurveyEventType } from '@kp/pulse-survey/models/pulse-survey-event.model';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { QuestBloc } from '@kp/quest/quest.bloc';
import { Answer } from '@kp/question-set/models/answer.model';
import { Question, QuestionType } from '@kp/question-set/models/question.model';
import { QuestionSetBloc } from '@kp/question-set/question-set.bloc';
import { CardRequestType } from '@kp/shared/components/cards/card-category/enums/card-request-type.enum';
import { CardItem } from '@kp/shared/components/cards/card-category/models/card-item.model';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { FirstVisitCase } from '@kp/shared/first-visit/first-visit-case.enum';
import { FirstVisitBloc } from '@kp/shared/first-visit/first-visit.bloc';
import { ReturnUrlService } from '@kp/shared/services/return-url.service';
import { UserBloc } from '@kp/user/user.bloc';
import { DataStoreService, productDisplayName, TitleService, WINDOW } from '@kservice';
import { ContentEventType } from '@ktypes/enums';
import { DataStatus, JsonObject, Status } from '@ktypes/models';
import { StringUtil, tryParseJSON } from '@kutil';
import isEqual from 'lodash/isEqual';
import { merge, skipWhile, Subject, switchMap } from 'rxjs';
import { catchError, skip, take, takeUntil, tap } from 'rxjs/operators';

const MIN_COUNT_FOR_PROGRESS_BAR = 3;

@Component({
  selector: 'kp-dialogue',
  templateUrl: './dialogue.component.html',
  styleUrls: ['./dialogue.component.scss'],
})
export class DialogueComponent implements OnInit, OnDestroy {
  @Input() cardRequestType: CardRequestType;
  @Input() dialogueKey: DialogueLogicKey | string;
  @Input() dialogueKeyType: KumanuKeyType;
  @Input() dialogueLogicKey: DialogueLogicKey;
  @Input() isEmbedded = false;
  @Input() returnUrl: string;
  @Input() title: string;

  animating = true;
  animationDoneMessage: string;
  closeableDialogue = false;
  dialogue: Dialogue;
  dialogueBlockType = DialogueBlockType;
  dialogueDataStatus: DataStatus<Dialogue>;
  dialogueId: string;
  displayProgressBar: boolean;
  isExternalPulseSurvey = false;
  isPulseSurvey = false;
  isQuestCard = false;
  isQuestionAnswered = false;
  isSubmitted = false;
  keysChecked = false;
  kumanuKeyType = KumanuKeyType;
  noValidKeys = true;
  promoId: string;
  productName = productDisplayName;
  status = Status;
  showAnimation = true;
  terminated = false;

  private _answer: Answer;
  private _analyticsJson: JsonObject = {};
  private _autoAdvanceEnabled = false;
  private _autoAdvanceTimeout: NodeJS.Timeout;
  private _complete = false;
  private _destroy$ = new Subject<void>();
  private _eventJson: JsonObject = {};
  private _firstReflectionCompletedStatus: boolean;
  private _question: Question;
  private _questionAnswered = false;
  private _questKey: string;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsBloc: AnalyticsBloc,
    private _authenticationBloc: AuthenticationBloc,
    private _cardBloc: CardBloc,
    private _contentEventBloc: ContentEventBloc,
    private _dataLinkBloc: DataLinkBloc,
    private _dataStoreService: DataStoreService,
    public dialogueBloc: DialogueBloc,
    private _firstVisitBloc: FirstVisitBloc,
    private _onboardingBloc: OnboardingBloc,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _questBloc: QuestBloc,
    private _returnUrlService: ReturnUrlService,
    private _router: Router,
    public questionSetBloc: QuestionSetBloc,
    private _titleService: TitleService,
    private _userBloc: UserBloc,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit(): void {
    if (this._onboardingBloc.isOnboarding) {
      this._onboardingBloc.setLatestPage('/welcome');
    }
    if (!this.isEmbedded) {
      this._titleService.set(this.title);
    }

    // first time through, check for an error, clear if exists
    const dialogueFirst$ = this.dialogueBloc.dialogue$.pipe(
      takeUntil(this._destroy$),
      take(1),
      tap(this._checkForError.bind(this)) //potential side effect of clearing _dialogue stream
    );
    // other times through, just process the change
    const dialogueRest$ = this.dialogueBloc.dialogue$.pipe(takeUntil(this._destroy$), skip(1));
    merge(dialogueFirst$, dialogueRest$)
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._handleDialogueChange.bind(this));

    this._firstVisitBloc.hasSeen$.pipe(takeUntil(this._destroy$)).subscribe((objStart) => {
      this._firstReflectionCompletedStatus = objStart.completedReflection;
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  async onSubmit(skipped = false) {
    if (this.isSubmitted) {
      // don't allow dialogue block to be submitted again if it was already submitted
      return;
    }
    this.isSubmitted = true;
    switch (this.dialogue?.dialogueBlock?.type) {
      case DialogueBlockType.question:
        if (this._question) {
          this._question.answer = this._answer;
          this._sendQuestionEndEvent();
          // Need to await due to possible race condition of dialogue block completing before question is answered
          // Same issue as with question sets
          await this.questionSetBloc.updateQuestion(this._question, skipped);
        }
        break;
      case DialogueBlockType.dialogue_block_signup_sequence:
        // handled in the DialogueSignupComponent
        break;
      case DialogueBlockType.terminate:
        this.terminated = true;
        break;
      case DialogueBlockType.information:
        break;
      default:
        break;
    }
    delete this._answer;
    this.dialogueBloc.updateBlockStatus(this.dialogue?.id, this.dialogue?.dialogueBlock?.id, this._eventJson);
  }

  onUndo() {
    this._autoAdvanceEnabled = false;
    if (this.dialogue?.dialogueBlock?.canUndo) {
      this.dialogueBloc.updateBlockStatus(
        this.dialogue?.id,
        this.dialogue?.dialogueBlock?.id,
        this._eventJson,
        DialogueBlockEventType.undone
      );
    }
  }

  closeDialogue() {
    this._pulseSurveyBloc.postSurveyEvent(PulseSurveyEventType.pulse_survey_popup_closed);
    this._pulseSurveyBloc.postSurveyEvent(PulseSurveyEventType.pulse_survey_closed_early);
    void this.completeDialogue(false);
  }

  async updateQuestionAnswer(answer: Answer) {
    if (this._questionAnswered && this.isSubmitted) {
      // Don't do anything if answered and being submitted
      return;
    }
    this._questionAnswered = true;
    clearTimeout(this._autoAdvanceTimeout);
    this._answer = answer;
    if (
      this._answer &&
      !isEqual(this._answer?.values, this.dialogue?.dialogueBlock?.question?.answer?.values) &&
      !this._autoAdvanceEnabled
    ) {
      this._autoAdvanceEnabled = true;
    }
    if ((this.isEmbedded && this._answer) || this._shouldAutoAdvance()) {
      // Awaiting submission to ensure no duplicate question on navigation
      await this.onSubmit();
      if (this.isEmbedded) {
        await this._router.navigate(['/dialogue'], {
          queryParams: {
            key: this.dialogueLogicKey,
            returnUrl: this._returnUrlService.savedUrl,
          },
        });
      }
      this._questionAnswered = false;
    }
  }

  /**
   * Handle any specific logic based on DialogueLogicKey, then run the default
   * tasks and transition away from the dialogue
   */
  async completeDialogue(event?: boolean) {
    // allow animation to be disabled via event
    this.animating = event;

    // daily reflection should trigger firstReflectionCompleted if it hasn't been yet
    if (DAILY_REFLECTION_KEYS.includes(this.dialogueLogicKey)) {
      this._firstVisitBloc.incrementFirstVisitValue(FirstVisitCase.checkInCount, 'completedCheckIns');
    }

    // dialogue_purposeful_onboarding_v1 should refresh the user data to get any updated user information
    // i.e. nickname
    if (this.dialogueLogicKey === DialogueLogicKey.dialogue_purposeful_onboarding_v1) {
      // intentionally exiting and not executing other dialogue completion tasks for onboarding
      return this._postPurposefulOnboardingDialogue();
    }

    // Handle any post-dialogue tasks (events, completions, etc.), unless a previous block exited early
    await this._performCompleteTasks();

    // Re-Enable submit
    this._reEnableSubmit();

    // Finally, transition away from dialogue
    this._postCompleteTransition();
  }

  /**
   * Refresh the user data to get updated user information
   * Wait for this to complete before moving on, and no need to execute other dialogueComplete tasks
   */
  private _postPurposefulOnboardingDialogue() {
    this._userBloc.fetchingUser$
      .pipe(
        skipWhile((fetchingUser) => fetchingUser?.status !== Status.starting && fetchingUser?.data),
        switchMap(() => this._dataStoreService.user$),
        take(1),
        catchError((err) => {
          // if there is an error, continue but note the error
          // this just gets any updates to user, but it will be requested again for pages that need it
          console.warn('There was an error attempting to get user post-dialogue', err);
          this._postCompleteTransition();
          return err;
        }),
        takeUntil(this._destroy$)
      )
      // currently we are just waiting for this call to complete before moving on, so don't need the user object
      .subscribe((/* user: User */) => {
        // got user, continue to transition away from onboarding dialogue
        this._postCompleteTransition();
      });
    this._userBloc.getUser(this._dataStoreService?.user?.id);
  }

  /**
   * Tasks for all dialogues to perform or check
   */
  private async _performCompleteTasks() {
    // Send promo_completed event if originally triggered from a promo
    if (this.promoId) {
      this._contentEventBloc.createContentEvent(this.promoId, ContentEventType.promo_completed);
    }

    // Update card completion status if appropriate
    await this._cardBloc.sendCardCompletionIfExistsInMap(
      this.dialogueKey as string,
      this.isQuestCard ? CardRequestType.QUEST : this.cardRequestType
    );

    // Update quest / questCard if appropriate
    if (this.isQuestCard) {
      this._questBloc.completeQuestIfDone(
        this._cardBloc.currentCompletionCardMapByKey[this.dialogueKey] as CardItem,
        this._questKey
      );
    }

    // Complete and clean up pulse survey if isPulseSurvey
    if (this.isPulseSurvey || this.isExternalPulseSurvey || DataLinkBloc.isExternalPulseSurvey) {
      this._pulseSurveyBloc.postSurveyEvent(PulseSurveyEventType.pulse_survey_completed, this.dialogue?.id);

      // Clean up after Pulse Survey
      // ensure pulse survey id is cleared
      this._pulseSurveyBloc.clearPulseSurveyId();
      // clear out any external pulse survey info if external
      if (DataLinkBloc.isExternalPulseSurvey) {
        this._clearPulseSurveyInfo();
      }
    }

    // Update firstReflectionCompleted status if appropriate (as long as not a PulseSurvey)
    if (!this._firstReflectionCompletedStatus && !this.isPulseSurvey) {
      this.questionSetBloc.firstReflectionCompleted();
    }
  }

  private _clearPulseSurveyInfo(): void {
    // ensure pulse survey data is cleared
    this._dataLinkBloc.clearLinkData();

    // clear out any external pulse survey info
    this._dataStoreService.setPulseSurveyAuthData(null);

    // restore saved auth data if exists (NOTE: do we need this?)
    this._authenticationBloc.restoreExistingAuthData();
  }

  /**
   * Transition away from dialogue after completion tasks are handled
   */
  private _postCompleteTransition() {
    // Only do this once
    if (!this._complete) {
      this._complete = true;
      // Dialogue stream must be cleared after navigation to ensure no new dialogue creation
      this._router.navigateByUrl(this._returnUrlService.savedUrl)?.then(() => {
        this.dialogueBloc.clearDialogueStreams();
      });
    }
  }

  private _checkForError(dialogueDataStatus) {
    if (dialogueDataStatus?.status === Status.error) {
      // TODO: Need to better handle errors, not just clear the stream to allow it to create a new one
      this.dialogueBloc.clearDialogueStreamsIfError();
    }
  }

  private _handleDialogueChange(dialogueDataStatus: DataStatus<Dialogue>) {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const { showPreviousAnswers } = queryParams;
    this._updateDialogueComponentProperties(queryParams, dialogueDataStatus);

    // Not using optional chaining on dialogueDataStatus.data due to
    // dialogueDataStatus?.data.logicKey !== this.dialogueKey
    // resolving to true if dialogueDataStatus.data is null
    if (
      !dialogueDataStatus ||
      (dialogueDataStatus.data != null && this._checkMatchingDialogueProperties(dialogueDataStatus))
    ) {
      return this._createNewDialogue(dialogueDataStatus, tryParseJSON(showPreviousAnswers));
    } else if (dialogueDataStatus?.status === Status.done && dialogueDataStatus.data != null) {
      return this._handleDialogueDataChange(dialogueDataStatus);
    } else if (
      dialogueDataStatus?.status === Status.error ||
      (dialogueDataStatus?.status !== Status.starting && dialogueDataStatus.data == null)
    ) {
      return this._handleDialogueErrorStatus(dialogueDataStatus);
    }
  }

  private _checkMatchingDialogueProperties(dialogueDataStatus): boolean {
    return (
      dialogueDataStatus.data.logicKey !== this.dialogueLogicKey ||
      (dialogueDataStatus.data.refKey != null && dialogueDataStatus.data.refKey !== this.dialogueKey) ||
      (dialogueDataStatus.data.id != null && dialogueDataStatus.data.id !== this.dialogueId && this.dialogueId != null)
    );
  }

  private _updateDialogueComponentProperties(queryParams: Params, dialogueDataStatus: DataStatus<Dialogue>) {
    // ensure if 'null' is passed (as a string) for the key params in a query string it is evaluated to actual `null`
    const queryKey = queryParams.key !== 'null' ? queryParams.key : null;
    const queryRefKey = queryParams.refKey !== 'null' ? queryParams.refKey : null;
    const queryLogicKey = queryParams.logicKey !== 'null' ? queryParams.logicKey : null;
    this.dialogueId = queryParams.dialogueId !== 'null' ? queryParams.dialogueId : null;
    this.promoId = queryParams.promoId !== 'null' ? queryParams.promoId : null;
    this.dialogueDataStatus = dialogueDataStatus;
    this.dialogueLogicKey = this._getDialogueLogicKey(dialogueDataStatus?.data, queryKey, queryLogicKey);
    this.dialogueKeyType = this._getDialogueKeyType(queryLogicKey);
    this.dialogueKey = this._getDialogueKey(dialogueDataStatus?.data, queryKey, queryRefKey);
    // default to TRACKING if not passed
    this.cardRequestType = (queryParams.cardRequestType as CardRequestType) || CardRequestType.TRACKING;
    // ensure if 'true' is passed (as a string) for the isPulseSurvey key it is evaluated to an actual boolean
    this.isPulseSurvey =
      typeof queryParams.isPulseSurvey === 'string' ? queryParams.isPulseSurvey === 'true' : queryParams.isPulseSurvey;
    // ensure if 'true' is passed (as a string) for the isExternalPulseSurvey key it is evaluated to an actual boolean
    this.isExternalPulseSurvey =
      typeof queryParams.isExternalPulseSurvey === 'string'
        ? queryParams.isExternalPulseSurvey === 'true'
        : queryParams.isExternalPulseSurvey ?? DataLinkBloc.isExternalPulseSurvey;
    this.isQuestCard = queryParams.isQuestCard;
    this._questKey = StringUtil.getKeyFromEncodedQueryString(queryParams.returnUrl, 'questKey', true);
    this.closeableDialogue = CLOSEABLE_DIALOGUES_KEYS.includes(this.dialogueLogicKey);
    this._setAnimationSettings(
      this.dialogueLogicKey in DialogueLogicKey
        ? (this.dialogueLogicKey as DialogueLogicKey)
        : DialogueLogicKey.templated_question_set_dialogue
    );
    this._returnUrlService.setSavedUrl(queryParams.returnUrl || '');
  }

  private _createNewDialogue(dialogueDataStatus: DataStatus<Dialogue>, showPreviousAnswers) {
    if (this.isPulseSurvey || this.isExternalPulseSurvey) {
      this._pulseSurveyBloc.postSurveyEvent(PulseSurveyEventType.pulse_survey_opened, dialogueDataStatus?.data?.id);
    }
    if (!this.dialogueId && (!this.dialogueLogicKey || !this.dialogueKey)) {
      // if either the logic key, a dialogue key, or dialogueId is falsey, create/get
      // the dialogue, it'll just fail, and complete without animation
      console.warn(
        `Invalid dialogue logic key (${this.dialogueLogicKey}), dialogue key (${this.dialogueKey}), or dialogue id (${this.dialogueId}) provided`
      );
      this.noValidKeys = true;
      return void this.completeDialogue(false);
    }
    this.keysChecked = true;
    if (this.dialogueId) {
      this.dialogueBloc.getExistingDialogue(this.dialogueId);
    } else {
      this.dialogueBloc.createDialogue(
        this.dialogueLogicKey,
        this.dialogueKeyType,
        this.dialogueKey,
        showPreviousAnswers || this.isQuestCard
      );
    }
    this._reEnableSubmit(dialogueDataStatus?.status);
  }

  private _handleDialogueDataChange(dialogueDataStatus: DataStatus<Dialogue>) {
    this.dialogue = dialogueDataStatus.data;
    this._updateProgressBar(dialogueDataStatus.data);
    this._window.scroll(0, 0);
    switch (this.dialogue?.dialogueBlock?.type) {
      case DialogueBlockType.question:
        this._question = this.dialogue?.dialogueBlock?.question;
        this._reEnableSubmit(dialogueDataStatus?.status);
        break;
      case DialogueBlockType.terminate:
        if (!this.showAnimation) {
          void this.completeDialogue(false);
        }
        break;
      default:
        this._reEnableSubmit(dialogueDataStatus?.status);
        break;
    }
  }

  private _handleDialogueErrorStatus(dialogueDataStatus: DataStatus<Dialogue>) {
    // on error or no data when not starting, send a console warning error and complete without animation
    console.warn(
      `Failed to create Dialogue - Likely invalid dialogue logic key (${this.dialogueLogicKey}) or dialogue key (${this.dialogueKey}) provided, or API error`
    );
    this.noValidKeys = true;
    this._reEnableSubmit(dialogueDataStatus?.status);
    return void this.completeDialogue(false);
  }

  // allow submissions again if this is called with anything other than starting and was previously submitted
  private _reEnableSubmit(status?: Status) {
    if (status !== Status.starting && this.isSubmitted) {
      this.isSubmitted = false;
    }
  }

  private _getDialogueLogicKey(dialogue: Dialogue, queryKey: string, queryLogicKey: string) {
    if (DAILY_REFLECTION_KEYS.includes(this.dialogueLogicKey) && this.isEmbedded) {
      return this.dialogueLogicKey;
    }
    if (this.dialogueLogicKey && (!dialogue?.logicKey || dialogue.logicKey === this.dialogueLogicKey)) {
      return this.dialogueLogicKey;
    }
    // For query params, `logicKey` is preferred, but QuestionSet deep links still send as `key` though
    // Prefer query param options to one stored in memory
    const dialogueLogicKey = (queryLogicKey || queryKey || dialogue?.logicKey) as DialogueLogicKey;
    if (dialogueLogicKey && !(dialogueLogicKey in DialogueLogicKey)) {
      // if the key exists but isn't one of the predefined DialogueLogicKeys,
      // assume it's a templated_question_set_dialogue
      return DialogueLogicKey.templated_question_set_dialogue;
    }
    return dialogueLogicKey;
  }

  private _getDialogueKeyType(queryLogicKey: string) {
    const dialogueLogicKey = (queryLogicKey || this.dialogueLogicKey) as DialogueLogicKey;
    if (dialogueLogicKey in DialogueLogicKey && !DIALOGUES_USING_REF_KEYS.includes(dialogueLogicKey)) {
      return KumanuKeyType.logicKey;
    }
    return KumanuKeyType.refKey;
  }

  private _getDialogueKey(dialogue: Dialogue, queryKey: string, queryRefKey: string) {
    if (DAILY_REFLECTION_KEYS.includes(this.dialogueKey as DialogueLogicKey) && this.isEmbedded) {
      return this.dialogueKey;
    }
    if (this.dialogueKey && (!dialogue?.refKey || dialogue.refKey === this.dialogueKey)) {
      return this.dialogueKey;
    }
    if (this.dialogueKeyType === KumanuKeyType.refKey && DIALOGUES_USING_REF_KEYS.includes(this.dialogueLogicKey)) {
      // For query params, `refKey` is preferred, but QuestionSet deep links may still send as `key` though
      return queryRefKey || queryKey || dialogue?.refKey;
    }
    return this.dialogueLogicKey;
  }

  private _sendQuestionEndEvent() {
    this._analyticsJson['questionId'] = this._question?.id;
    this._analyticsJson[KumanuKeyType.logicKey] = this.dialogueLogicKey;
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromEvent({ event: AnalyticEvent.question_end, meta: this._analyticsJson })
    );
  }

  private _setAnimationSettings(key: DialogueLogicKey) {
    switch (key) {
      case DialogueLogicKey.dialogue_daily_reflection_v1:
      case DialogueLogicKey.focus_aware_daily_checkin_dialogue:
        this.animationDoneMessage = 'Check-in Complete!';
        break;
      case DialogueLogicKey.dialogue_purposeful_onboarding_v1:
      case DialogueLogicKey.dialogue_resourceful_onboarding_v1:
        this.showAnimation = false;
        break;
      case DialogueLogicKey.templated_question_set_dialogue:
        this.animationDoneMessage = 'Done!';
        break;
    }
  }

  private _shouldAutoAdvance(): boolean {
    return (
      ([
        QuestionType.slider,
        QuestionType.likert,
        QuestionType.image_icon_selection,
        QuestionType.radio_button,
      ].includes(this.dialogue?.dialogueBlock?.question?.inputType) ||
        ([QuestionType.word_list, QuestionType.image_tile, QuestionType.image_word_list].includes(
          this.dialogue?.dialogueBlock?.question?.inputType
        ) &&
          this.dialogue?.dialogueBlock?.question?.settings?.countMax === 1)) &&
      this._dataStoreService.authData?.user?.settings?.autoAdvance &&
      this._answer?.values?.[0]?.value != null &&
      this._autoAdvanceEnabled
    );
  }

  private _updateProgressBar(dialogue: Dialogue) {
    this.displayProgressBar = this.isPulseSurvey && dialogue?.progress?.totalCount > MIN_COUNT_FOR_PROGRESS_BAR;
  }
}
